import type React from 'react'
import { useRef, useEffect } from 'react'
import { Form, Input, Select, InputNumber } from 'antd'
import type { FormListFieldData } from 'antd'
import { GraviButton, Horizontal, Vertical, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import type { InvoiceFormValues, InvoiceProduct } from '@api/useSupplierReconciliation/types/invoice'
import { useFormatter } from '@api/useFormatter'
import { useInvoiceValidation } from '../hooks/useInvoiceValidation'
import { useInvoiceCalculations } from '../hooks/useInvoiceCalculations'
import { ProductOption } from './ProductOption'
import {
  numberFormatter,
  currencyParser,
  volumeFormatter,
  volumeParser,
  currencyFormatterWithDecimals,
  currencyParserWithDecimals,
  volumeFormatterThreeDecimals,
  currencyFormatterTwoDecimals,
  robustParser,
} from '../utils/formatters'
import { taxColumns } from '../utils/constants'

interface InvoiceTaxesSectionProps {
  form: Form<InvoiceFormValues>
  fields: FormListFieldData[]
  add: (defaultValues?: Record<string, unknown>) => void
  remove: (index: number) => void
  dropdownOptions: {
    suppliers: Array<{ value: string; label: string }>
    terminals: Array<{ value: string; label: string }>
    products: Array<{ value: string; label: string }>
    states: Array<{ value: string; label: string }>
  }
  onCalculateTotals: () => void
}

export const InvoiceTaxesSection: React.FC<InvoiceTaxesSectionProps> = ({
  form,
  fields,
  add,
  remove,
  dropdownOptions,
  onCalculateTotals,
}) => {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  const { getTaxDescriptionRules, getTaxTypeRules, getTaxAuthorityRules } = useInvoiceValidation()

  const { handleTaxProductSelect, updateTaxAmount, updateTaxPerUnit, updateTaxTotal } = useInvoiceCalculations()

  // Ref to track when we should focus on the newly added row
  const shouldFocusNewRow = useRef(false)
  const lastFieldsLength = useRef(fields.length)

  // Effect to focus on Tax Description input when a new tax is added
  useEffect(() => {
    if (shouldFocusNewRow.current && fields.length > lastFieldsLength.current) {
      // Focus on the Tax Description input of the last added row
      const lastIndex = fields.length - 1
      const lastField = fields[lastIndex]
      if (lastField) {
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          const taxDescriptionInput = document.querySelector(`input[id*="taxes_${lastField.name}_taxDescription"]`)
          if (taxDescriptionInput) {
            ;(taxDescriptionInput as HTMLInputElement).focus()
          }
        }, 100)
      }
      shouldFocusNewRow.current = false
    }
    lastFieldsLength.current = fields.length
  }, [fields.length])

  const handleAddTax = () => {
    shouldFocusNewRow.current = true
    add({})
  }

  const renderTaxFieldComponent = (column: (typeof taxColumns)[0], field: FormListFieldData, index: number) => {
    switch (column.key) {
      case 'taxDescription':
        return <Input placeholder='Tax Description' style={{ width: '100%' }} autoComplete='off' />

      case 'taxType':
        return <Input placeholder='Tax Type' style={{ width: '100%' }} autoComplete='off' />

      case 'product':
        const products = form.getFieldValue('products') || []
        const uniqueProducts = products
          .filter((p: InvoiceProduct) => p && p.product)
          .reduce((acc: InvoiceProduct[], current: InvoiceProduct) => {
            if (!acc.find((item) => item.product === current.product)) {
              acc.push(current)
            }
            return acc
          }, [])

        const fieldProductOptions = uniqueProducts.map((p: InvoiceProduct) => {
          const productInfo = dropdownOptions?.products?.find((mp) => mp.value === p.product)
          return {
            value: p.product,
            label: (
              <ProductOption
                product={productInfo?.label || p.product}
                grossVol={p.grossVol || 0}
                netVol={p.netVol || 0}
              />
            ),
          }
        })

        return (
          <Select
            placeholder='Select product'
            allowClear
            showSearch={false}
            options={fieldProductOptions}
            onChange={(value) => handleTaxProductSelect(index, value, form)}
            style={{ width: '100%' }}
          />
        )

      case 'taxAuthority':
        return <Input placeholder='Tax Authority' style={{ width: '100%' }} autoComplete='off' />

      case 'amount':
        return (
          <InputNumber
            placeholder='Amount'
            min={0}
            precision={3}
            style={{ width: '100%' }}
            stringMode
            formatter={volumeFormatterThreeDecimals}
            parser={robustParser}
            onChange={(value) => {
              // Check if the input currently shows special patterns but onChange received converted values
              const inputElement = document.activeElement as HTMLInputElement
              const inputValue = inputElement?.value

              // If input shows ".0" but onChange got "0", preserve the ".0"
              if (inputValue === '.0' && value === '0') {
                updateTaxAmount(index, '.0', form)
              }
              // If input shows "-.0" but onChange got "0", preserve the "-.0"
              else if (inputValue === '-.0' && value === '0') {
                updateTaxAmount(index, '-.0', form)
              } else {
                updateTaxAmount(index, value, form)
              }

              onCalculateTotals()
            }}
          />
        )

      case 'perUnit':
        return (
          <InputNumber
            placeholder='Per Unit'
            precision={6}
            prefix={currencySymbol}
            style={{ width: '100%' }}
            stringMode
            formatter={currencyFormatterWithDecimals}
            parser={robustParser}
            onChange={(value) => {
              // Check if the input currently shows special patterns but onChange received converted values
              // This happens when InputNumber converts patterns like "-0" to 0 or ".0" to 0
              const inputElement = document.activeElement as HTMLInputElement
              const inputValue = inputElement?.value

              // If input shows "-0" but onChange got "0", preserve the "-0"
              if (inputValue === '-0' && value === '0') {
                updateTaxPerUnit(index, '-0', form)
              }
              // If input shows ".0" but onChange got "0", preserve the ".0"
              else if (inputValue === '.0' && value === '0') {
                updateTaxPerUnit(index, '.0', form)
              }
              // If input shows "-.0" but onChange got "0", preserve the "-.0"
              else if (inputValue === '-.0' && value === '0') {
                updateTaxPerUnit(index, '-.0', form)
              } else {
                updateTaxPerUnit(index, value, form)
              }

              onCalculateTotals()
            }}
          />
        )

      case 'total':
        return (
          <InputNumber
            placeholder='Total'
            precision={2}
            prefix={currencySymbol}
            style={{ width: '100%' }}
            stringMode
            formatter={currencyFormatterTwoDecimals}
            parser={robustParser}
            onChange={(value) => {
              // Check if the input currently shows special patterns but onChange received converted values
              // This happens when InputNumber converts patterns like "-0" to 0 or ".0" to 0
              const inputElement = document.activeElement as HTMLInputElement
              const inputValue = inputElement?.value

              // If input shows "-0" but onChange got "0", preserve the "-0"
              if (inputValue === '-0' && value === '0') {
                updateTaxTotal(index, '-0', form)
              }
              // If input shows ".0" but onChange got "0", preserve the ".0"
              else if (inputValue === '.0' && value === '0') {
                updateTaxTotal(index, '.0', form)
              }
              // If input shows "-.0" but onChange got "0", preserve the "-.0"
              else if (inputValue === '-.0' && value === '0') {
                updateTaxTotal(index, '-.0', form)
              } else {
                updateTaxTotal(index, value, form)
              }

              onCalculateTotals()
            }}
          />
        )

      default:
        return null
    }
  }

  const getValidationRules = (column: (typeof taxColumns)[0]) => {
    if (!column.required) return []

    switch (column.key) {
      case 'taxDescription':
        return getTaxDescriptionRules()
      case 'taxType':
        return getTaxTypeRules()
      case 'taxAuthority':
        return getTaxAuthorityRules()
      default:
        return [{ required: true, message: `Enter ${column.header.toLowerCase()}` }]
    }
  }

  return (
    <Vertical gap={16} className='p-3' border='bottom'>
      <Vertical gap={16} style={{ width: '100%' }}>
        {fields.length > 0 && (
          <Vertical gap={8} style={{ width: '100%' }}>
            <Horizontal gap={16} style={{ paddingBottom: 8, borderBottom: '1px solid var(--gray-200)' }}>
              {taxColumns.map((column) => (
                <div key={column.key} style={{ flex: parseFloat(column.flex), margin: 0 }}>
                  <Texto weight='bold'>
                    {column.header}
                    {column.required ? ' *' : ''}
                  </Texto>
                </div>
              ))}
              <div style={{ width: 32 }} />
            </Horizontal>

            <Horizontal border='bottom' style={{ width: '100%' }} />

            {fields.map((field, index) => (
              <Horizontal key={field.key} gap={16} verticalCenter style={{ paddingTop: 4, paddingBottom: 4 }}>
                {taxColumns.map((column) => (
                  <Form.Item
                    key={`${field.key}-${column.key}`}
                    style={{
                      marginBottom: 4,
                      flex: parseFloat(column.flex),
                      minHeight: 48,
                      alignSelf: 'flex-start',
                    }}
                    name={[field.name, column.key]}
                    rules={getValidationRules(column)}
                  >
                    {renderTaxFieldComponent(column, field, index)}
                  </Form.Item>
                ))}

                <div style={{ width: 32 }}>
                  <GraviButton
                    appearance='outlined'
                    error
                    onClick={() => remove(field.name)}
                    icon={<MaterialIcon icon='delete' />}
                  />
                </div>
              </Horizontal>
            ))}
          </Vertical>
        )}
        <GraviButton appearance='outlined' theme2 onClick={handleAddTax} block icon={<MaterialIcon icon='add' />}>
          Add Tax
        </GraviButton>
      </Vertical>
    </Vertical>
  )
}
